import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов для сравнения.
          Сохраните расчет после выполнения, чтобы он появился в этом разделе.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {getCalculationWord(savedCalculations.length)}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Налоговый период:</span>
                <span className="font-medium">
                  {calculation.formData.taxationYear}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Доходы:</span>
                <span>
                  {formatCurrency(calculation.results.totalIncome)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Налогооблагаемая база:</span>
                <span>
                  {formatCurrency(calculation.results.taxableIncome)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Налог к уплате:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calculation.results.totalTaxAmount)}
                </span>
              </div>
              <div className="flex justify-between mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                <span>Эффективная ставка:</span>
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  {formatNumber(calculation.results.effectiveTaxRate, 2)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length > 1 && (
        <div className="mt-5 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2">
            Сравнение эффективной ставки
          </h4>
          {savedCalculations.map((calculation) => (
            <div key={`rate-${calculation.id}`} className="mb-2 last:mb-0">
              <div className="flex justify-between text-xs text-indigo-600 dark:text-indigo-400 mb-1">
                <span>{calculation.name}</span>
                <span>{formatNumber(calculation.results.effectiveTaxRate, 2)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-indigo-500 dark:bg-indigo-400 h-2 rounded-full"
                  style={{ width: `${Math.min(100, calculation.results.effectiveTaxRate * 5)}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const getCalculationWord = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'расчетов';
  }
  
  if (lastDigit === 1) {
    return 'расчет';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'расчета';
  }
  
  return 'расчетов';
};

export default ComparisonSidebar;